import type { IntlShape } from 'react-intl'
import type { TripDay, TripStop } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { getDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { appendGooglePlaceContext } from 'tripPlanner/utils/googlePlaceContext'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import { showAddingToTripSnackbar } from './addToTripSnackbar'

interface SendAddPlaceToDayParams {
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  intl: IntlShape
  placeName: string
  placeId?: string
  stop: TripStop
  day: Pick<TripDay, 'id' | 'dayNumber' | 'date'>
  sendMessageOptions?: SendMessageOptions
}

/**
 * Adds a place/offer to a specific itinerary day. Shared by the add-to-trip day picker and
 * the direct "date already matches an itinerary day" shortcut so both build the same message
 * and selectedDayContext.
 */
export function sendAddPlaceToDay({
  onSendMessage,
  intl,
  placeName,
  placeId,
  stop,
  day,
  sendMessageOptions,
}: SendAddPlaceToDayParams) {
  const displayDayLabel = getLocalisedDayLabel(intl, day.dayNumber, day.date)
  const contextDayLabel = getDayLabel(day.dayNumber, day.date)
  const displayMessage = intl.formatMessage(
    {
      id: 'jBmVfW0CoK4BYmKj1gXx',
      defaultMessage: 'Add "{placeName}" to {dayLabel} in {cityName}',
      description: 'User chat message shown when adding a place to a specific day and city in the trip planner',
    },
    {
      placeName,
      dayLabel: displayDayLabel,
      cityName: formatPlacePhrase({ location: { name: stop.placeName }, language: intl.locale, intent: 'location' }),
    },
  )
  const taggedMessage = appendContextTag(
    displayMessage,
    appendGooglePlaceContext(`Add "${placeName}" to ${contextDayLabel} in ${stop.placeName}`, placeId),
    TRIP_PLANNER_ACTION_CONTEXT_LABEL,
  )

  showAddingToTripSnackbar(intl, placeName)

  onSendMessage(taggedMessage, {
    ...sendMessageOptions,
    selectedDayContext: {
      tripStopId: stop.id,
      startDayId: day.id,
      endDayId: day.id,
      startDayNumber: day.dayNumber,
      endDayNumber: day.dayNumber,
    },
  })
}
